import React from 'react';
import { Box, Theme, Typography } from '@material-ui/core';
import { createStyles, makeStyles, styled } from '@material-ui/core/styles';

import { StyledButton } from '@bizapp-lp/workflow/molecules/StyledButton';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    description: {
      color: '#ffffff',
      textShadow: '0 0 8px rgba(0, 0, 0, 0.2)',
      margin: theme.spacing(10, 0, 6),
      maxWidth: '566px',
      width: '100%',
      [theme.breakpoints.down(960)]: {
        margin: '0 auto',
      },
      '& .MuiTypography-body1': {
        fontWeight: 'bold',
      },
    },
    descriptionButton: {
      width: '100%',
      height: '48px',
    },
    grow: {
      flexGrow: 1,
      [theme.breakpoints.up(1170)]: {
        width: '64px',
        flexGrow: 0,
      },
    },
  }),
);

export interface ClosingProps {
  className?: string;
  onClick1?: () => void;
  onClick2?: () => void;
}

const Component: React.FC<ClosingProps> = ({
  className,
  onClick1,
  onClick2,
}) => {
  const classes = useStyles();

  const RootContainer = styled('section')(({ theme }) => ({
    boxSizing: 'border-box',
    color: '#333333',
    margin: 0,
    padding: theme.spacing(6, 0, 0),
  }));

  const ContentContainer = styled('div')(({ theme }) => ({
    background:
      'transparent linear-gradient(to right, #0EC6BD 0%, #78DB99 100%) 0% 0% no-repeat padding-box',
    margin: 0,
    minHeight: '464px',
    display: 'block',
    padding: theme.spacing(0, 2, 10),
    [theme.breakpoints.up(960)]: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-end',
      padding: theme.spacing(0, 5),
    },
  }));

  const PricingBox = styled('div')(({ theme }) => ({
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.3)',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    width: '100%',
    maxWidth: '420px',
    margin: '0 auto',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: theme.spacing(0, 0, 2.5),
    position: 'relative',
    top: '-48px',
    [theme.breakpoints.up(960)]: {
      margin: theme.spacing(-6, 5, 4, 0),
      padding: theme.spacing(0),
      height: '480px',
      position: 'initial',
      top: 'initial',
    },
    '&:before': {
      content: '""',
      display: 'block',
      background:
        'transparent linear-gradient(to right, #0EC6BD 0%, #78DB99 100%) 0% 0% no-repeat padding-box',
      height: '6px',
      width: '100%',
    },
  }));

  const PricingImgBox = styled('div')(({ theme }) => ({
    '& > img': {
      margin: '42px 0 24px',
      width: '80px',
    },
  }));

  const HeadingComponent = styled('h2')(({ theme }) => ({
    color: '#333333',
    fontSize: '20px',
    lineHeight: '30px',
    fontWeight: 'bold',
    [theme.breakpoints.up(768)]: {
      fontSize: '26px',
      lineHeight: '39px',
    },
  }));

  const CatchphraseComponent = styled('p')(({ theme }) => ({
    color: '#333333',
    margin: theme.spacing(3, 0, 7),
    padding: theme.spacing(0, 2),
    fontSize: '16px',
    lineHeight: '22px',
    '& span': {
      display: 'block',
    },
  }));

  const PricingComponent = styled('p')(({ theme }) => ({
    color: '#333333',
    fontSize: '16px',
    textAlign: 'left',
    '& > span': {
      display: 'block',
    },
    '& strong': {
      fontSize: '48px',
      lineHeight: '62px',
      fontWeight: 'bold',
      [theme.breakpoints.up(768)]: {
        fontSize: '64px',
        lineHeight: '96px',
      },
      '& > span': {
        fontSize: '20px',
        lineHeight: '30px',
        fontWeight: 'normal',
        [theme.breakpoints.up(768)]: {
          fontSize: '24px',
          lineHeight: '36px',
        },
      },
    },
  }));

  const DescriptionBox = styled('div')(({ theme }) => ({
    margin: '0 auto',
    maxWidth: '566px',
    width: '100%',
    textShadow: '0 0 8px #00000033',
    [theme.breakpoints.up(960)]: {
      margin: theme.spacing(10, 0, 10),
    },
  }));

  const DescriptionTextComponent = styled('p')(({ theme }) => ({
    color: '#ffffff',
    fontSize: '16px',
    lineHeight: '24px',
    fontWeight: 'bold',
    margin: theme.spacing(4, 0, 2),
    '& > span': {
      display: 'block',
    },
  }));

  return (
    <RootContainer className={className}>
      <ContentContainer>
        <PricingBox>
          <PricingImgBox>
            <img
              src={`${process.env.PUBLIC_URL}/images/workflow.png`}
              alt="Workflow Icon"
            />
          </PricingImgBox>
          <Typography component={HeadingComponent}>Workflow</Typography>
          <Typography component={CatchphraseComponent}>
            <span>ノーコードで現場で簡単に利用することができる</span>
            <span>ワークフローサービス</span>
          </Typography>
          <Typography component={PricingComponent}>
            <span>1人あたり</span>
            <strong>
              330 <span>円/月 (税込) ～</span>
            </strong>
          </Typography>
        </PricingBox>
        <Box className={classes.grow} />
        <DescriptionBox>
          <Typography component={DescriptionTextComponent}>まずは</Typography>
          <StyledButton
            variant="primary"
            label="資料ダウンロード"
            className={classes.descriptionButton}
            onClick={onClick1}
          />
          <Typography component={DescriptionTextComponent}>もしくは</Typography>
          <StyledButton
            variant="secondary"
            label="お問い合わせ"
            onClick={onClick2}
            className={classes.descriptionButton}
          />
        </DescriptionBox>
      </ContentContainer>
    </RootContainer>
  );
};

export const Closing = React.memo(Component);
